import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import useDimensions from 'react-use-dimensions';

import './style.css';

const DEFAULT_PROPS = {
  pivot: { x: 0.5, y: 0.5 },
  offset: { x: 0, y: 0 },
};

const pipe = ({ startX, startY, endX, endY, curve }) => {
  const deltaX = (endX - startX) * curve;
  const deltaY = (endY - startY) * curve;
  // for further calculations which ever is the shortest distance
  const delta = deltaY < Math.abs(deltaX) ? deltaY : Math.abs(deltaX);

  let arc1 = 0;
  let arc2 = 1;
  if (startX > endX) {
    arc1 = 1;
    arc2 = 0;
  }

  return `M${startX} ${startY} V${
    startY + delta
  } A${delta} ${delta} 0 0 ${arc1} ${startX + delta * Math.sign(deltaX)} ${
    startY + 2 * delta
  } H${
    endX - delta * Math.sign(deltaX)
  } A${delta} ${delta} 0 0 ${arc2} ${endX} ${startY + 3 * delta} V${endY}`;
};

const Connection = ({ A, B, threshold, duration, stroke, radius, curve }) => {
  const [refStart, start] = useDimensions();
  const [refEnd, end] = useDimensions();
  useEffect(() => {
    const el = document.querySelector(A.query);
    if (!el) {
      console.warn(`Couldn't find node with query ${A.query}`);
    }
    refStart(el);
  });

  useEffect(() => {
    const el = document.querySelector(B.query);
    if (!el) {
      console.warn(`Couldn't find node with query ${B.query}`);
    }
    refEnd(el);
  });

  if (!start.x || !end.x) return null;

  const a = { ...DEFAULT_PROPS, ...A };
  const b = { ...DEFAULT_PROPS, ...B };

  start.x += a.offset.x;
  start.y += a.offset.y;

  end.x += b.offset.x;
  end.y += b.offset.y;

  const { scrollY } = window;
  const { scrollX } = window;

  const startX = start.x + start.width * a.pivot.x + scrollX;
  const startY = start.y + start.height * a.pivot.y + scrollY;

  let endX = end.x + end.width * b.pivot.x + scrollX;
  let endY = end.y + scrollY + end.height * b.pivot.y;

  const width = window.innerWidth;
  const height = document.body.scrollHeight;

  if (Math.abs(startX - endX) < threshold.x) {
    endX = startX;
  }

  if (Math.abs(startY - endY) < threshold.y) {
    endY = startY;
  }

  const path = pipe({ startX, startY, endX, endY, curve });

  return (
    <svg height={height} width={width} className="connection">
      <path
        d={path}
        stroke="currentColor"
        strokeWidth={stroke}
        fill="none"
        strokeLinecap="round"
      />
      <circle cx="0" cy="0" r={radius} fill="var(--color-main-04)">
        <animateMotion
          dur={`${duration}s`}
          repeatCount="indefinite"
          path={path}
        />
      </circle>
    </svg>
  );
};

Connection.propTypes = {
  A: PropTypes.shape({
    query: PropTypes.string.isRequired,
    pivot: PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    }),
    offset: PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    }),
  }).isRequired,
  B: PropTypes.shape({
    query: PropTypes.string.isRequired,
    pivot: PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    }),
    offset: PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    }),
  }).isRequired,
  threshold: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }),
  duration: PropTypes.number,
  stroke: PropTypes.number,
  radius: PropTypes.number,
  curve: PropTypes.number,
};

Connection.defaultProps = {
  curve: 0.1,
  duration: 10,
  radius: 6,
  stroke: 5,
  threshold: { x: 0, y: 0 },
};

export default Connection;
